import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Trash2,
  Search,
  Users,
  Megaphone,
  MessageSquare,
  Gavel,
  Eye,
  MousePointerClick,
  UserPlus,
  FileText,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from "lucide-react";
import { AdminShell } from "@/components/AdminShell";
import { useConfirm } from "@/components/admin/ConfirmDialog";
import { AdminTooltip } from "@/components/admin/AdminTooltip";
import { DailyReportCard } from "@/components/admin/DailyReportCard";
import { DateCell } from "@/components/admin/DateCell";
import { DraftAds } from "@/components/admin/DraftAds";
import { AdminTodoBoard } from "@/components/admin/AdminTodoBoard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { categoryLabel } from "@/lib/categories";
import {
  getAdminStats,
  listAdminAds,
  adminDeleteAd,
  getAdminPulse,
} from "@/lib/admin.functions";
import { adminSeedDemoMessages, adminRemoveDemoUsers } from "@/lib/demo-messages.functions";
import { getAuctionTrafficStatus, setAuctionsEnabled } from "@/lib/auction-launch.functions";
import { getAuctionsEnabled } from "@/lib/auction-availability.functions";

export const Route = createFileRoute("/_authenticated/admin/")({
  head: () => ({
    meta: [
      { title: "Administration — Sellavo" },
      {
        name: "description",
        content: "Intern administration för Sellavo: annonser, användare och nyckeltal.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminPage,
});

function Stat({
  icon: Icon,
  label,
  value,
  filter,
  help,
  hint,
}: {
  icon: any;
  label: string;
  value: string | number;
  filter?: "all" | "paused" | "sold";
  help?: string;
  hint?: string;
}) {
  const body = (
    <>
      <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <Icon className="h-4 w-4" /> {label}
      </p>
      <p className="mt-2 font-heading text-2xl font-bold">{value}</p>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </>
  );
  const card = filter ? (
    <Link
      to="/admin/annonser"
      search={{ filter }}
      className="block rounded-md border border-border bg-card p-4 transition-colors hover:border-primary hover:bg-accent"
    >
      {body}
    </Link>
  ) : (
    <div className="rounded-md border border-border bg-card p-4">{body}</div>
  );
  return help ? <AdminTooltip text={help}>{card}</AdminTooltip> : card;
}

/** Dagens puls: besök, registreringar och sådant som väntar på svar. */
function TodayPulse() {
  const fetchPulse = useServerFn(getAdminPulse);
  const { data } = useQuery({
    queryKey: ["admin-pulse"],
    queryFn: () => fetchPulse({}),
    refetchInterval: 60_000,
  });
  const v = (n?: number) => (data ? (n ?? 0).toLocaleString("sv-SE") : "–");
  const diff =
    data && data.visitorsYesterday
      ? Math.round(((data.visitorsToday - data.visitorsYesterday) / data.visitorsYesterday) * 100)
      : null;

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-xl font-semibold">Idag</h2>
        <Link to="/admin/statistik" className="text-sm font-medium text-primary hover:underline">
          Se hela statistiken
        </Link>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          icon={Eye}
          label="Besökare idag"
          value={v(data?.visitorsToday)}
          hint={
            diff === null
              ? data
                ? `Igår: ${data.visitorsYesterday}`
                : undefined
              : `${diff > 0 ? "+" : ""}${diff} % mot igår (${data?.visitorsYesterday})`
          }
          help="Antal unika enheter som besökt sajten sedan midnatt. Egna adminbesök räknas inte med."
        />
        <Stat
          icon={MousePointerClick}
          label="Sidvisningar idag"
          value={v(data?.pageviewsToday)}
          help="Antal sidladdningar totalt idag. Fler sidvisningar per besökare betyder att folk klickar sig vidare."
        />
        <Stat
          icon={UserPlus}
          label="Nya konton idag"
          value={v(data?.signupsToday)}
          help="Antal användare som registrerat sig sedan midnatt."
        />
        <Stat
          icon={Megaphone}
          label="Nya annonser idag"
          value={v(data?.adsToday)}
          help="Antal annonser som publicerats sedan midnatt."
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">

        <AdminTooltip text="Meddelanden mellan köpare och säljare det senaste dygnet som ännu inte lästs av mottagaren.">
          <div className="rounded-md border border-border bg-card p-4">
            <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <MessageSquare className="h-4 w-4" /> Olästa meddelanden (24 h)
            </p>
            <p className="mt-2 font-heading text-2xl font-bold">{v(data?.unreadMessages)}</p>
          </div>
        </AdminTooltip>
        <AdminTooltip text="Alla publika sidor på sajten – guider, kategorisidor, ortssidor och jämförelser.">
          <Link
            to="/admin/sidor"
            className="block rounded-md border border-border bg-card p-4 transition-colors hover:border-primary hover:bg-accent"
          >
            <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <FileText className="h-4 w-4" /> Alla sidor
            </p>
            <p className="mt-2 font-heading text-base font-semibold">Se sajtens innehåll</p>
          </Link>
        </AdminTooltip>
      </div>
    </section>
  );
}


/** Auktioner är avstängda publikt tills sajten når 200 unika besökare per dygn. */
function AuctionLaunchCard() {
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const fetchStatus = useServerFn(getAuctionTrafficStatus);
  const fetchEnabled = useServerFn(getAuctionsEnabled);
  const setEnabled = useServerFn(setAuctionsEnabled);

  const q = useQuery({
    queryKey: ["auction-traffic-status"],
    queryFn: () => fetchStatus({}),
    staleTime: 5 * 60_000,
  });
  const enabledQ = useQuery({
    queryKey: ["auctions-enabled"],
    queryFn: () => fetchEnabled(),
  });
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => setEnabled({ data: { enabled } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["auctions-enabled"] }),
  });

  const s = q.data;
  const on = enabledQ.data?.enabled === true;
  if (!s) return null;

  return (
    <section
      className={`rounded-md border p-4 ${
        on || s.reached ? "border-primary bg-primary/10" : "border-border bg-card"
      }`}
    >
      <h2 className="flex items-center gap-2 font-heading text-xl font-semibold">
        <Gavel className="h-5 w-5" />
        {on
          ? "Auktioner är påslagna"
          : s.reached
            ? "Nu har vi 200 besökare om dagen – slå igång auktioner!"
            : "Auktioner: väntar på trafik"}
      </h2>
      <p className="mt-1 text-sm text-foreground/75">
        {on
          ? "Auktioner visas för alla besökare just nu."
          : `Auktioner är dolda för besökarna tills du slår på dem här. Målet är ${s.goal} unika besökare på ett dygn.`}{" "}
        Bästa dygnet hittills:{" "}
        <span className="font-semibold">
          {s.bestDay ? `${s.bestDay.visitors} besökare (${s.bestDay.day})` : "inga besök ännu"}
        </span>
        . Idag: {s.today}.
      </p>
      <div className="mt-3">
        <Button
          size="sm"
          variant={on ? "outline" : "default"}
          disabled={toggle.isPending || enabledQ.isLoading}
          onClick={async () => {
            if (!on) {
              const ok = await confirm({
                title: "Slå på auktioner publikt?",
                description:
                  "Auktioner blir direkt synliga för alla besökare och säljare kan välja auktion i säljflödet.",
                confirmLabel: "Slå på auktioner",
              });
              if (!ok) return;
            }
            toggle.mutate(!on);
          }}
        >
          <Gavel className="mr-2 h-4 w-4" />
          {on ? "Stäng av auktioner" : "Slå på auktioner"}
        </Button>
      </div>
      {dialog}
    </section>
  );
}

function AdminPage() {

  return (
    <AdminShell title="Översikt">
      <AdminBody />
    </AdminShell>
  );
}

type AdSortKey = "title" | "category" | "location" | "price" | "created_at";

function AdminBody() {
  const { confirm, dialog } = useConfirm();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<AdSortKey>("created_at");
  const [dir, setDir] = useState<"asc" | "desc">("desc");

  const toggleSort = (key: AdSortKey) => {
    if (key === sort) setDir((d) => (d === "desc" ? "asc" : "desc"));
    else {
      setSort(key);
      setDir("desc");
    }
  };

  const Th = ({ label, k }: { label: string; k: AdSortKey }) => (
    <th className="whitespace-nowrap px-3 py-2">
      <button
        type="button"
        onClick={() => toggleSort(k)}
        className={`inline-flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-foreground ${
          sort === k ? "text-foreground" : "text-muted-foreground"
        }`}
      >
        {label}
        {sort === k ? (
          dir === "asc" ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-50" />
        )}
      </button>
    </th>
  );

  const fetchStats = useServerFn(getAdminStats);
  const fetchAds = useServerFn(listAdminAds);
  const removeAd = useServerFn(adminDeleteAd);

  const stats = useQuery({ queryKey: ["admin-stats"], queryFn: () => fetchStats({}) });
  const ads = useQuery({
    queryKey: ["admin-ads", search, sort, dir],
    queryFn: () => fetchAds({ data: { search: search || undefined, sort, dir } }),
  });

  const del = useMutation({
    mutationFn: (slug: string) => removeAd({ data: { slug } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-ads"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
    },
  });

  const seedFn = useServerFn(adminSeedDemoMessages);
  const removeDemoFn = useServerFn(adminRemoveDemoUsers);
  const seed = useMutation({
    mutationFn: () => seedFn({ data: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-stats"] }),
  });
  const removeDemo = useMutation({
    mutationFn: () => removeDemoFn({}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-stats"] }),
  });

  return (
    <div className="space-y-10">
      <AdminTodoBoard />

      <TodayPulse />

      <DailyReportCard />

      <AuctionLaunchCard />

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          icon={Megaphone}
          label="Annonser totalt"
          value={stats.data?.ads ?? "–"}
          filter="all"
          help="Alla annonser i databasen, även pausade och sålda. Klicka för att öppna listan."
        />
        <Stat
          icon={MessageSquare}
          label="Meddelanden"
          value={stats.data?.messages ?? "–"}
          help="Totalt antal meddelanden som skickats mellan köpare och säljare sedan start."
        />

        <Stat
          icon={Megaphone}
          label="Nya senaste 7 dagarna"
          value={stats.data?.adsLastWeek ?? "–"}
          help="Antal annonser som publicerats den senaste veckan – visar takten på sajten."
        />
        <Stat
          icon={Users}
          label="Användare"
          value={stats.data?.users ?? "–"}
          help="Totalt antal registrerade konton."
        />
      </section>

      <section className="rounded-md border border-border bg-card p-4">
        <h2 className="font-heading text-xl font-semibold">Demo-meddelanden</h2>
        <p className="mt-1 text-sm text-foreground/75">
          Skapar tre fiktiva användare (taggade som demo) som skickar var sitt meddelande om din
          senaste annons. Går att rensa bort helt när som helst.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button size="sm" disabled={seed.isPending} onClick={() => seed.mutate()}>
            <MessageSquare className="mr-2 h-4 w-4" /> Simulera tre meddelanden
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={removeDemo.isPending}
            onClick={async () => {
              const ok = await confirm({
                title: "Ta bort demo-användare?",
                description:
                  "Alla demo-användare och deras meddelanden raderas. Det går inte att ångra.",
                confirmLabel: "Ta bort demo",
              });
              if (ok) removeDemo.mutate();
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" /> Ta bort demo-användare
          </Button>
        </div>
        {seed.isError && (
          <p className="mt-2 text-sm text-destructive">{(seed.error as Error).message}</p>
        )}
        {seed.isSuccess && (
          <p className="mt-2 text-sm text-foreground/75">
            Tre meddelanden skickade om ”{seed.data.adTitle}”. Öppna{" "}
            <Link to="/meddelanden" className="font-medium text-primary hover:underline">
              inkorgen
            </Link>
            .
          </p>
        )}
        {removeDemo.isSuccess && (
          <p className="mt-2 text-sm text-foreground/75">
            {removeDemo.data.removed} demo-användare borttagna.
          </p>
        )}
      </section>

      <section>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-heading text-xl font-semibold">Annonser</h2>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Sök titel"
                className="h-10 w-56 pl-9"
              />
            </div>
          </div>
        </div>

        <div className="mt-4 overflow-x-auto rounded-md border border-border">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <Th label="Titel" k="title" />
                <Th label="Kategori" k="category" />
                <Th label="Ort" k="location" />
                <Th label="Pris" k="price" />
                <Th label="Skapad" k="created_at" />
                <th className="whitespace-nowrap px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {(ads.data?.rows ?? []).map((ad: any) => (
                <tr
                  key={ad.slug}
                  className="border-t border-border transition-colors hover:bg-muted/50"
                >
                  <td className="max-w-[280px] break-words px-3 py-2">
                    <Link
                      to="/a/$slug"
                      params={{ slug: ad.slug }}
                      className="font-medium hover:underline"
                    >
                      {ad.title}
                    </Link>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                    {categoryLabel(ad.category ?? "ovrigt")}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{ad.location}</td>
                  <td className="whitespace-nowrap px-3 py-2">{ad.price?.toLocaleString("sv-SE")} kr</td>
                  <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                    <DateCell value={ad.created_at} />
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    <Link
                      to="/admin/annons/$slug"
                      params={{ slug: ad.slug }}
                      className="mr-2 text-sm font-medium text-primary hover:underline"
                    >
                      Historik
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        const ok = await confirm({
                          title: "Ta bort annonsen?",
                          description: `”${ad.title}” tas bort permanent. Det går inte att ångra.`,
                          confirmLabel: "Ta bort annonsen",
                        });
                        if (ok) del.mutate(ad.slug);
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
              {ads.data?.rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                    Inga annonser matchade.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <DraftAds />

      <section>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-heading text-xl font-semibold">Användare</h2>
          <Link
            to="/admin/anvandare"
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-accent"
          >
            <Users className="h-4 w-4" /> Hantera alla användare
          </Link>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Sök bland alla konton, se kontotyp och sätt Plus eller Pro för en vald period.
        </p>
      </section>
      {dialog}
    </div>
  );
}
